import { useMemo } from 'react';
import { useNextSpeciesDetailQuery } from 'src/app/portfolio/views/next/state/api/nextSpeciesGraphSlice';
import type { NextSpeciesDetail } from 'src/app/portfolio/views/next/state/api/nextSpeciesQuery';
import { confidenceDetails } from 'src/shared/helpers/speciesConfidence';

/** One site's prediction of a species, as the species info modal renders it. */
export type SpeciesDetailAssessment = {
    projectName: string;
    country: string;
    isInvasive: boolean;
    /** Lowercase, because the badge lookup is keyed that way. */
    predictionLevel: string;
    hasEDNAData: boolean;
    hasUploadedData: boolean;
    foundInEDNA: boolean;
    foundInUploadedData: boolean;
};

export type SpeciesDetailResult = {
    scientificName: string;
    assessments: SpeciesDetailAssessment[];
};

type Args = {
    customerId: string;
    scientificName: string;
    filters?: { name: string; values: string[] }[];
};

/**
 * The species info modal's data. The grid is the only source since DD-5858;
 * it calls the collection `sites` and renames most of its fields, normalised
 * here so the modal keeps the shape it always rendered.
 */
export const useSpeciesDetail = ({ customerId, scientificName, filters }: Args) => {
    const args = { customerId, scientificName, ...(filters?.length ? { filters } : {}) };
    const grid = useNextSpeciesDetailQuery(args, { skip: !customerId || !scientificName });

    // Memoised because the modal drives a useEffect from this and sets table state in
    // it; a fresh object each render restarts that chain indefinitely.
    const data = useMemo(() => (grid.currentData ? toDetail(grid.currentData) : null), [grid.currentData]);

    return { data, isFetching: grid.isFetching };
};

const toDetail = (detail: NextSpeciesDetail): SpeciesDetailResult => ({
    scientificName: detail.scientificName,
    assessments: detail.sites.map(site => ({
        projectName: site.siteName,
        country: site.country,
        isInvasive: site.invasive,
        predictionLevel: badgeLevel(site.confidence),
        hasEDNAData: site.hasEDNAData,
        hasUploadedData: site.hasUploadedData,
        foundInEDNA: site.foundInEDNA,
        foundInUploadedData: site.foundInUploadedData,
    })),
});

/**
 * The grid's enum carries UNKNOWN, which the badge table has no entry for — left raw
 * it resolves to undefined and the modal throws reading .colorClass. Anything the
 * table does not know degrades to the lowest level rather than taking the modal down.
 */
const badgeLevel = (confidence: string): string => {
    const level = confidence.toLowerCase();
    return confidenceDetails[level] ? level : 'low';
};
